import { useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import styled from "@emotion/styled";
import { Tooltip } from "@/components/ui/overlays/Tooltip";

export type SideNavSubItem = {
  id: string;
  label: ReactNode;
  onSelect?: () => void;
};

export type SideNavItem = {
  id: string;
  label: ReactNode;
  /** Leading icon node. */
  icon?: ReactNode;
  active?: boolean;
  /** Trailing badge/slot (hidden when collapsed). */
  badge?: ReactNode;
  /** Hover flyout entries. When present, clicking the row does not call `onSelect`. */
  nested?: SideNavSubItem[];
  onSelect?: () => void;
};

export type SideNavSection = {
  id?: string;
  /** Uppercase section title (hidden when collapsed). */
  title?: ReactNode;
  items: SideNavItem[];
  /** Optional action row shown under the items (e.g. "Create new"). Hidden when collapsed. */
  action?: ReactNode;
};

export type SideNavProps = {
  sections: SideNavSection[];
  collapsed?: boolean;
  /** Expanded width in px. Defaults to 240. */
  width?: number;
  /** Collapsed width in px. Defaults to 56. */
  collapsedWidth?: number;
  /** Top slot, above the scrollable nav (e.g. a primary CTA). */
  header?: ReactNode;
  /** Extra content rendered inside the scroll area after the sections (e.g. a recents list). */
  children?: ReactNode;
  /** Bottom slot (e.g. user + settings). */
  footer?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

/* Exact compiled output of the pre-migration Tailwind utilities. The
 * pre-existing dead class `bfrs-[scrollbar-width:none]` (never emitted by
 * Tailwind) is intentionally not reproduced. Verified against the frozen
 * baseline. */

const SideNavRowButton = styled("button", {
  label: "AgenticSideNav-row",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { collapsed: boolean; active: boolean } }>(({ ownerState }) => [
  {
    marginBottom: ".125rem",
    display: "flex",
    width: "100%",
    alignItems: "center",
    borderRadius: "var(--bfrs-radius-lg)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty: "color,background-color,border-color,text-decoration-color,fill,stroke",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s"
  },
  ownerState.collapsed
    ? { justifyContent: "center", gap: "0px", paddingLeft: 0, paddingRight: 0, paddingTop: ".625rem", paddingBottom: ".625rem" }
    : { height: "2rem", gap: ".5rem", paddingLeft: ".625rem", paddingRight: ".625rem" },
  ownerState.active
    ? {
        "--tw-bg-opacity": "1",
        backgroundColor: "hsl(var(--bfrs-color-surface-app) / var(--tw-bg-opacity, 1))"
      }
    : {
        "&:hover": {
          backgroundColor: "hsl(var(--bfrs-color-primary) / .04)"
        }
      }
]);

const SideNavRowIcon = styled("span", {
  label: "AgenticSideNav-rowIcon",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { active: boolean } }>(({ ownerState }) => [
  {
    display: "flex",
    flexShrink: 0,
    alignItems: "center"
  },
  ownerState.active
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
      }
    : {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
      }
]);

const SideNavRowLabel = styled("span", {
  label: "AgenticSideNav-rowLabel",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { active: boolean } }>(({ ownerState }) => [
  {
    flex: "1 1 0%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
    fontSize: "12px"
  },
  ownerState.active
    ? {
        fontWeight: 400,
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
      }
    : {
        fontWeight: 400,
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
      }
]);

const SideNavRowCaret = styled("svg", {
  label: "AgenticSideNav-rowCaret"
})({
  flexShrink: 0,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SideNavRowWrap = styled("div", {
  label: "AgenticSideNav-rowWrap"
})({
  position: "relative"
});

const SideNavFlyout = styled("div", {
  label: "AgenticSideNav-flyout"
})({
  position: "fixed",
  left: "15rem",
  zIndex: 1000,
  minWidth: "200px",
  overflow: "hidden",
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-shadow": "var(--bfrs-shadow-menu)",
  "--tw-shadow-colored": "var(--bfrs-shadow-menu)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
});

const SideNavFlyoutList = styled("div", {
  label: "AgenticSideNav-flyoutList"
})({
  paddingTop: ".25rem",
  paddingBottom: ".25rem"
});

const SideNavFlyoutItem = styled("button", {
  label: "AgenticSideNav-flyoutItem"
})({
  display: "flex",
  width: "100%",
  alignItems: "center",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".5rem",
  paddingBottom: ".5rem",
  textAlign: "left",
  fontSize: "13px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
  transitionProperty: "color,background-color,border-color,text-decoration-color,fill,stroke",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
  }
});

function SideNavRow({ item, collapsed }: { item: SideNavItem; collapsed: boolean }) {
  const [flyoutTop, setFlyoutTop] = useState<number | null>(null);
  const hasNested = !!item.nested?.length;

  const open = (event: MouseEvent<HTMLButtonElement>) => {
    if (!hasNested || collapsed) return;
    setFlyoutTop(event.currentTarget.getBoundingClientRect().top);
  };

  const row = (
    <SideNavRowButton
      type="button"
      onClick={() => (hasNested ? undefined : item.onSelect?.())}
      onMouseEnter={open}
      ownerState={{ collapsed, active: Boolean(item.active) }}
    >
      {item.icon && <SideNavRowIcon ownerState={{ active: Boolean(item.active) }}>{item.icon}</SideNavRowIcon>}
      {!collapsed && (
        <>
          <SideNavRowLabel ownerState={{ active: Boolean(item.active) }}>{item.label}</SideNavRowLabel>
          {item.badge}
          {hasNested && (
            <SideNavRowCaret width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M4.5 3l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </SideNavRowCaret>
          )}
        </>
      )}
    </SideNavRowButton>
  );

  return (
    <SideNavRowWrap onMouseLeave={() => setFlyoutTop(null)}>
      {/* When collapsed the label is hidden, so surface it as a right-side tooltip on the whole row. */}
      {collapsed ? (
        <Tooltip content={item.label} side="right">
          {row}
        </Tooltip>
      ) : (
        row
      )}

      {hasNested && !collapsed && flyoutTop !== null && (
        <SideNavFlyout
          style={{ top: flyoutTop }}
          onMouseEnter={() => setFlyoutTop(flyoutTop)}
          onMouseLeave={() => setFlyoutTop(null)}
        >
          <SideNavFlyoutList>
            {item.nested!.map((sub) => (
              <SideNavFlyoutItem
                key={sub.id}
                type="button"
                onClick={() => { sub.onSelect?.(); setFlyoutTop(null); }}
              >
                {sub.label}
              </SideNavFlyoutItem>
            ))}
          </SideNavFlyoutList>
        </SideNavFlyout>
      )}
    </SideNavRowWrap>
  );
}

const SideNavRoot = styled("aside", {
  label: "AgenticSideNav-root"
})({
  position: "relative",
  display: "flex",
  height: "100%",
  flexShrink: 0,
  flexDirection: "column",
  overflow: "hidden",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))"
});

const SideNavHeader = styled("div", {
  label: "AgenticSideNav-header"
})({
  flexShrink: 0,
  paddingLeft: ".5rem",
  paddingRight: ".5rem",
  paddingBottom: ".25rem",
  paddingTop: ".75rem"
});

const SideNavScroll = styled("div", {
  label: "AgenticSideNav-scroll",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { collapsed: boolean } }>(({ ownerState }) => [
  {
    flex: "1 1 0%",
    overflowY: "auto",
    paddingBottom: "1rem",
    paddingTop: ".5rem"
  },
  ownerState.collapsed
    ? { paddingLeft: ".5rem", paddingRight: ".5rem" }
    : { paddingLeft: "15px", paddingRight: "15px" }
]);

const SideNavSectionWrap = styled("div", {
  label: "AgenticSideNav-section",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { divided: boolean } }>(({ ownerState }) => [
  ownerState.divided && {
    marginTop: ".5rem",
    borderTopWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border-light) / var(--tw-border-opacity, 1))",
    paddingTop: ".5rem"
  }
]);

const SideNavSectionTitle = styled("div", {
  label: "AgenticSideNav-sectionTitle"
})({
  WebkitUserSelect: "none",
  MozUserSelect: "none",
  userSelect: "none",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingBottom: "3px",
  paddingTop: ".625rem",
  fontSize: "10px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "1.08px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const SideNavSectionAction = styled("div", {
  label: "AgenticSideNav-sectionAction"
})({
  marginTop: ".25rem"
});

const SideNavFooter = styled("div", {
  label: "AgenticSideNav-footer"
})({
  flexShrink: 0,
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))"
});

/**
 * Collapsible application sidebar navigation. Renders grouped sections of items
 * with active state, optional badges, and hover flyouts for nested items, plus
 * dedicated `header` and `footer` slots. All data (labels, icons, handlers) is
 * passed in — the component owns layout, collapse behavior, and chrome only.
 */
export function SideNav({
  sections,
  collapsed = false,
  width = 216,
  collapsedWidth = 56,
  header,
  children,
  footer,
  className,
  style
}: SideNavProps) {
  const resolvedWidth = collapsed ? collapsedWidth : width;

  return (
    <SideNavRoot
      className={className}
      style={{
        width: resolvedWidth,
        minWidth: resolvedWidth,
        borderRight: "1px solid hsl(var(--bfrs-color-border-strong))",
        transition: "width 0.22s cubic-bezier(0.4,0,0.2,1), min-width 0.22s cubic-bezier(0.4,0,0.2,1)",
        ...style
      }}
    >
      {header && <SideNavHeader>{header}</SideNavHeader>}

      <SideNavScroll ownerState={{ collapsed }}>
        {sections.map((section, index) => (
          <SideNavSectionWrap key={section.id ?? index} ownerState={{ divided: index > 0 }}>
            {!collapsed && section.title && <SideNavSectionTitle>{section.title}</SideNavSectionTitle>}
            {section.items.map((item) => (
              <SideNavRow key={item.id} item={item} collapsed={collapsed} />
            ))}
            {!collapsed && section.action && <SideNavSectionAction>{section.action}</SideNavSectionAction>}
          </SideNavSectionWrap>
        ))}
        {!collapsed && children}
      </SideNavScroll>

      {footer && <SideNavFooter>{footer}</SideNavFooter>}
    </SideNavRoot>
  );
}

SideNav.displayName = "SideNav";
